<template>
  <v-card flat>
    <v-card-text
      v-if="loaded && !exists"
      class="py-6"
    >
      <v-alert
        text
        color="info"
        class="mb-0"
      >
        Ожидание оплаты по этому заказу не запускалось. Оно заводится только на
        переводе — по QR или платёжной ссылке; наличным и оплате при получении оно
        не нужно.
      </v-alert>
    </v-card-text>

    <template v-else>
      <v-card-text class="d-flex align-center flex-wrap pb-0">
        <div class="d-flex align-center flex-wrap order-workflow__head">
          <v-chip
            small
            label
            :color="status.color"
            class="v-chip-light-bg me-3"
            :class="`${status.color}--text`"
          >
            {{ status.text }}
          </v-chip>
          <span class="text--secondary me-4">{{ definition }} · версия {{ version }}</span>
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex align-center">
          <v-btn
            outlined
            small
            class="me-3"
            :loading="loading"
            @click="refresh()"
          >
            <v-icon
              size="18"
              class="me-2"
            >
              {{ icons.mdiRefresh }}
            </v-icon>
            <span>Обновить</span>
          </v-btn>
          <v-btn
            color="primary"
            small
            :disabled="!canSuspend"
            @click="suspend()"
          >
            <span>Остановить</span>
          </v-btn>
        </div>
      </v-card-text>

      <v-card-text>
        <v-simple-table dense>
          <tbody>
            <tr
              v-for="row in summary"
              :key="row.title"
            >
              <td class="text--secondary order-workflow__title">
                {{ row.title }}
              </td>
              <td>{{ row.value }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-text>
        <p class="font-weight-medium text--primary mb-3">
          Шаги
        </p>

        <v-expansion-panels
          accordion
          multiple
        >
          <v-expansion-panel
            v-for="step in steps"
            :key="step.id"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center flex-wrap order-workflow__step">
                <v-chip
                  small
                  label
                  :color="stepStatus(step.status).color"
                  class="v-chip-light-bg me-3"
                  :class="`${stepStatus(step.status).color}--text`"
                >
                  {{ stepStatus(step.status).text }}
                </v-chip>
                <span class="font-weight-medium me-4">{{ stepName(step.name) }}</span>
                <span class="text--secondary me-4">{{ period(step) }}</span>
                <span
                  v-if="step.sleepUntil"
                  class="text--secondary me-4"
                >до {{ dateTime(step.sleepUntil) }}</span>
                <span
                  v-if="step.retryCount"
                  class="error--text"
                >повторов: {{ step.retryCount }}</span>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-simple-table dense>
                <tbody>
                  <tr
                    v-for="row in stepRows(step)"
                    :key="row.title"
                  >
                    <td class="text--secondary order-workflow__title">
                      {{ row.title }}
                    </td>
                    <td class="order-workflow__value">
                      {{ row.value }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mdiRefresh } from '@mdi/js'
import { toDateTimeString } from '@/helpers/index'
import { stepName, stepStatus, workflowStatus } from '@/helpers/workflow'

export default {
  name: 'OrderWorkflow',
  async mounted() {
    await this.refresh()
  },
  data: () => ({
    icons: { mdiRefresh },
  }),
  computed: {
    ...mapState('orderWorkflow', [
      'exists',
      'id',
      'definition',
      'version',
      'statusRaw',
      'startedAt',
      'finishedAt',
      'nextRunAt',
      'data',
      'steps',
      'loading',
      'loaded',
    ]),
    status() {
      return workflowStatus(this.statusRaw)
    },
    canSuspend() {
      return this.exists && this.statusRaw === 'Runnable'
    },
    summary() {
      return [
        { title: 'Идентификатор', value: this.id },
        { title: 'Запущено', value: toDateTimeString(this.startedAt) },
        { title: 'Завершено', value: toDateTimeString(this.finishedAt) },
        { title: 'Следующая проверка', value: toDateTimeString(this.nextRunAt) },
        { title: 'Данные схемы', value: this.data },
      ].filter(row => row.value)
    },
  },
  methods: {
    ...mapActions('orderWorkflow', ['fetchWorkflow', 'suspendWorkflow']),
    stepName,
    stepStatus,
    dateTime: toDateTimeString,
    async refresh() {
      const { id } = this.$route.params
      await this.fetchWorkflow(id)
    },
    async suspend() {
      const { id } = this.$route.params
      await this.suspendWorkflow(id)
      await this.refresh()
    },
    period(step) {
      const from = toDateTimeString(step.startedAt)
      if (!from) return 'не начинался'
      const to = toDateTimeString(step.finishedAt)

      return to ? `${from} — ${to}` : `с ${from}`
    },

    stepRows(step) {
      return [
        { title: 'Событие', value: step.eventName },
        { title: 'Ключ события', value: step.eventKey },
        { title: 'Событие получено', value: step.eventPublished ? 'да' : null },
        { title: 'Тело события', value: step.eventData },
        { title: 'Результат', value: step.outcome },
        { title: 'Состояние шага', value: step.persistenceData },
        { title: 'Контекст', value: step.contextItem },
        { title: 'Область', value: step.scope },
        { title: 'Дочерние', value: step.children },
        { title: 'Предыдущий шаг', value: step.predecessorId },
        { title: 'Номер шага в схеме', value: step.stepId },
        { title: 'Идентификатор', value: step.id },
      ].filter(row => row.value !== null && row.value !== undefined && row.value !== '')
    },
  },
}
</script>

<style lang="scss" scoped>
.order-workflow__head,
.order-workflow__step {
  row-gap: 4px;
}

.order-workflow__title {
  width: 220px;
  vertical-align: top;
}

.order-workflow__value {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.8125rem;
}
</style>
